/**
 * ConstraintSliders - Infrastructure and deployment constraint controls
 */

import { Slider } from '@/components/ui/slider';
import type { WizardEnvironment } from '@/stores/wizardStore';
import { PRECISION_OPTIONS, findPrecisionOption } from '@/lib/sizingOptions';

type ConstraintSlidersProps = {
  environment: WizardEnvironment;
  onChange: (updates: Partial<WizardEnvironment>) => void;
};

const SliderRow = ({
  label,
  description,
  value,
  onValueChange,
  min,
  max,
  step = 1,
  display,
}: {
  label: string;
  description: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  display: string;
  onValueChange: (value: number) => void;
}) => (
  <div className="p-4 rounded-lg border bg-card">
    <div className="flex items-center justify-between gap-3 mb-3">
      <div>
        <p className="text-sm font-semibold">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <span className="text-sm font-semibold text-primary">{display}</span>
    </div>
    <Slider
      min={min}
      max={max}
      step={step}
      value={[value]}
      onValueChange={(vals) => onValueChange(vals[0] ?? value)}
      aria-label={label}
    />
  </div>
);

export function ConstraintSliders({ environment, onChange }: ConstraintSlidersProps) {
  const sensitivityMap = { low: 1, medium: 2, high: 3 };
  const latencyMap = { snappy: 1, balanced: 2, batch: 3 };
  
  const sensitivityValue = sensitivityMap[environment.regulatorySensitivity];
  const latencyValue = latencyMap[environment.latencyTolerance];

  const precisionIndex = PRECISION_OPTIONS.findIndex((opt) => opt.value === environment.precision);
  const precisionDescription = findPrecisionOption(environment.precision);

  const handleSensitivityChange = (value: number) => {
    const map = ['low', 'medium', 'high'] as const;
    onChange({ regulatorySensitivity: map[value - 1] || 'medium' });
  };

  const handleLatencyChange = (value: number) => {
    const map = ['snappy', 'balanced', 'batch'] as const;
    onChange({ latencyTolerance: map[value - 1] || 'balanced' });
  };

  const handlePrecisionChange = (index: number) => {
    const clamped = Math.max(0, Math.min(PRECISION_OPTIONS.length - 1, Math.round(index)));
    onChange({ precision: PRECISION_OPTIONS[clamped].value as 4 | 8 | 16 });
  };

  return (
    <div className="space-y-4">
      {/* Latency Tolerance */}
      <SliderRow
        label="Response Time Requirements"
        description="What response time do your users expect?"
        value={latencyValue}
        min={1}
        max={3}
        step={1}
        display={
          latencyValue === 1 
            ? 'Snappy (<1s)' 
            : latencyValue === 2 
            ? 'Balanced (1-3s)' 
            : 'Batch (async OK)'
        }
        onValueChange={handleLatencyChange}
      />

      {/* User Experience (Batch) */}
      <SliderRow
        label="Throughput vs Responsiveness"
        description="Lower batch = snappier first token; higher batch = better throughput."
        value={environment.batch}
        min={1}
        max={256}
        display={`Batch ${environment.batch}`}
        onValueChange={(val) => onChange({ batch: Math.round(val) })}
      />

      {/* Precision Target */}
      <div className="p-4 rounded-lg border bg-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-semibold">Model Precision</p>
            <p className="text-xs text-muted-foreground">
              Quantization impacts throughput, quality, and memory footprint.
            </p>
          </div>
          <span className="text-sm font-semibold text-primary">{precisionDescription.label}</span>
        </div>
        <div className="space-y-3">
          <Slider
            min={0}
            max={PRECISION_OPTIONS.length - 1}
            step={1}
            value={[precisionIndex]}
            onValueChange={([value]) => handlePrecisionChange(value ?? precisionIndex)}
            aria-label="Precision target"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            {PRECISION_OPTIONS.map((option) => (
              <span key={option.value}>{option.label}</span>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{precisionDescription.description}</p>
        </div>
      </div>
    </div>
  );
}
